import { capture } from "@/lib/tracking/capture";
import { getTenantInfo, getDatastores, getCompanyInfo } from "@/helpers/apis";
import { updateTenantInfo, updateDatastores, setCompanyInfo } from "./appSlice";
import { cacheLoginMode } from "@/helpers/loginConfig";
import { Dispatch, useState } from "react";
import { UnknownAction } from "@reduxjs/toolkit";
import { useAppState } from "./useAppContext";
import { getTenant } from "@/helpers/tenant";
import { setFreemiumMode } from "@/helpers/freemiumMode";

const useAppActions = (dispatch: Dispatch<UnknownAction>) => {
  const { userGroup, loggedinUser } = useAppState();

  const [loadingTenantInfo, setLoadingTenantInfo] = useState(true);
  const [loadingDatastores, setLoadingDatastores] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const fetchTenantInfo = () => {
    return new Promise<void>(resolve => {
      setLoadingTenantInfo(true);
      getTenantInfo()
        .then(response => {
          capture("tenant_info", { response });
          if (response) {
            // Before dispatch: AppProvider gates its children on this fetch, so
            // every isFreeStack() caller downstream sees the tenant's value.
            setFreemiumMode(!!response.freemiumModeEnabled);
            // Keeps the pre-login mode cache fresh for signed-in users off a
            // request they already make. Only a real boolean is written: an
            // older backend that omits the field must not clobber a cached true.
            if (typeof response.ssoLoginEnabled === "boolean") {
              cacheLoginMode(getTenant(), response.ssoLoginEnabled);
            }
            dispatch(updateTenantInfo(response));
          }
          resolve();
        })
        .catch(err => {
          // Resolve rather than reject: the app bootstrap invokes this bare, so a
          // rejection escapes as an unhandled promise rejection carrying the raw
          // API detail string, which is reported as a non-Error value. Bootstrap
          // failure degrades to tenant defaults. The one awaited caller only
          // logs the error, so its behavior is unchanged.
          console.error("Failed to fetch tenant info:", err);
          resolve();
        })
        .finally(() => {
          setLoadingTenantInfo(false);
        });
    });
  };

  const fetchDatastores = () => {
    return new Promise<void>(resolve => {
      setLoadingDatastores(true);
      getDatastores()
        .then(response => {
          dispatch(updateDatastores(response?.data_stores));
          resolve();
        })
        .catch(err => {
          console.error("Failed to fetch datastores:", err);
          resolve();
        })
        .finally(() => {
          setLoadingDatastores(false);
        });
    });
  };

  const fetchCompanies = () => {
    return new Promise<void>(resolve => {
      setLoadingCompanies(true);
      getCompanyInfo()
        .then(response => {
          if (response) {
            dispatch(setCompanyInfo(response));
          }
          resolve();
        })
        .catch(err => {
          console.error("Failed to fetch company info:", err);
          resolve(); // Don't reject, just log the error
        })
        .finally(() => {
          setLoadingCompanies(false);
        });
    });
  };

  const getCommonHeaders = () => {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${loggedinUser?.auth_token || ""}`,
      "X-Tenant": getTenant(),
    };

    if (userGroup) {
      headers["X-User-Group-Id"] = userGroup.id;
    }

    return headers;
  };

  return {
    fetchTenantInfo,
    fetchDatastores,
    fetchCompanies,
    loadingTenantInfo,
    loadingDatastores,
    loadingCompanies,
    setLoadingTenantInfo,
    setLoadingDatastores,
    setLoadingCompanies,
    getCommonHeaders,
  };
};

export default useAppActions;
